// Motion helpers: reduced-motion detection (system setting or the player's preference) and viewport hooks.
import { useEffect, useState } from "react";
import { usePrefs } from "./session.js";
import { isNativeApp, nativeHaptic } from "../../lib/native.js";

const mq = (q: string) => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(q) : null);

export function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => mq(query)?.matches ?? false);
  useEffect(() => {
    const m = mq(query);
    if (!m) return;
    const h = () => setOn(m.matches);
    h();
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, [query]);
  return on;
}

export function useReducedMotion(): boolean {
  const sys = useMedia("(prefers-reduced-motion: reduce)");
  const prefs = usePrefs();
  return sys || prefs.reduceMotion;
}

/** The app column's width (`--app-w` in tokens.css: 430, or 600 on an upright tablet since D-132). */
function appMaxWidth(): number {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--app-w"));
  return Number.isFinite(v) && v > 0 ? v : 430;
}
/**
 * The play screens pad for the status bar and the home bar, so they get the height between them (D-127; the table's
 * buttons ran 46 px off a 402 × 874 iPhone). D-143: not only in the iPhone and Android apps: the home-screen web app
 * draws under the home bar too, and its table scrolled by up to 92 px. An ordinary browser tab measures both as 0.
 */
function usableHeight(): number {
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;visibility:hidden;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)";
  document.body.append(probe);
  const cs = getComputedStyle(probe);
  const insets = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  probe.remove();
  return window.innerHeight - insets;
}

/** The window's height, for layouts that must fit a short laptop screen (desktop table). */
export function useViewportHeight(): number {
  const [h, setH] = useState(() => (typeof window !== "undefined" ? usableHeight() : 900));
  useEffect(() => {
    const f = () => setH(usableHeight());
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return h;
}

export function useViewportWidth(): number {
  // The app column is centred at `--app-w` (styles/base.css), so the table is laid out for that width, not the window.
  const [w, setW] = useState(() => (typeof window !== "undefined" ? Math.min(window.innerWidth, appMaxWidth()) : 402));
  useEffect(() => {
    const h = () => setW(Math.min(window.innerWidth, appMaxWidth()));
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

/** The whole window's width, for layouts that span it (a phone on its side, D-125). */
export function useWindowWidth(): number {
  const [w, setW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 402));
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

/** Landscape phone: wide and short. The table swaps to the reed-mat layout. */
export function useLandscape(): boolean {
  return useMedia("(orientation: landscape) and (max-height: 500px)");
}

/** Desktop: 900 px and up. The table gets the icon rail, the 640 oval and the results-and-chat dock. */
export { useDesktop } from "../../lib/useDesktop";

/**
 * Chrome refuses (and logs) vibrate() before the page's first tap (#63): a turn that starts before you have touched the
 * screen stays silent instead of filling the console.
 */
export const canVibrate = (): boolean => {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return false;
  const activation = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
  return activation ? activation.hasBeenActive : true;
};
export const haptic = (pattern: number | number[]): void => {
  // In the iPhone/Android app (D-126) the strongest pulse of the pattern becomes a native impact.
  if (isNativeApp()) { nativeHaptic(Math.max(...[pattern].flat())); return; }
  try { if (canVibrate()) navigator.vibrate(pattern); } catch { /* ignore */ }
};
